import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  SchoolOutlined,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/Api';
import logo from '../../assets/school_logo.png';

const normalizeTeacherUser = (user) => {
  if (!user) return user;

  const id = user._id || user.id || user.userId;
  let school = user.school;

  if (typeof school === 'string') {
    school = { _id: school, id: school, name: user.schoolName };
  } else if (school && typeof school === 'object') {
    const schoolId = school._id || school.id || school.schoolId;
    school = {
      ...school,
      _id: schoolId,
      id: schoolId,
      name: school.name || school.schoolName,
    };
  }

  return {
    ...user,
    _id: id,
    id,
    school,
    role: user.role || 'teacher',
  };
};

const fetchTeacherProfile = async (baseUser) => {
  let mergedUser = normalizeTeacherUser(baseUser);

  try {
    const profileRes = await api.get('/profile');
    mergedUser = normalizeTeacherUser({
      ...mergedUser,
      ...profileRes.data,
      profileImage: profileRes.data?.profilePicture || mergedUser.profileImage,
    });
  } catch (err) {
    // Login can still continue; feature pages can retry profile enrichment.
  }

  try {
    const teacherRes = await api.get(`/teachers/by-user/${mergedUser._id}`);
    const teacher = teacherRes.data?.teacher || teacherRes.data;

    if (teacher?._id) {
      mergedUser = normalizeTeacherUser({
        ...mergedUser,
        ...teacher,
        teacherId: teacher._id,
      });

      try {
        const classesRes = await api.get(`/teachers/${teacher._id}/classes`);
        mergedUser.assignedClasses = classesRes.data?.classes || [];
      } catch (err) {
        mergedUser.assignedClasses = mergedUser.assignedClasses || [];
      }
    }
  } catch (err) {
    mergedUser.assignedClasses = mergedUser.assignedClasses || [];
  }

  return mergedUser;
};

export default function TeacherPortalLogin() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.03)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.05)',
      },
      '& fieldset': {
        borderWidth: '1px',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      },
      '&:hover fieldset': {
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(0, 0, 0, 0.2)',
      },
      '&.Mui-focused': {
        backgroundColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(0, 0, 0, 0.05)',
        boxShadow: '0 0 0 4px rgba(10, 132, 255, 0.15)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#0a84ff',
        borderWidth: '1px',
      },
      '& .MuiInputBase-input': {
        padding: '16.5px 14px',
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError('Enter your teacher email and password.');
      return;
    }

    setLoading(true);

    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      const loginRes = await api.post('/auth/login', {
        email: email.trim(),
        password,
        role: 'teacher',
      });

      const { token, user } = loginRes.data || {};

      if (!token || !user) {
        throw new Error('Incorrect email or password.');
      }

      if (user.role !== 'teacher') {
        throw new Error('This portal is only for teacher accounts.');
      }

      localStorage.setItem('token', token);
      const fullUser = await fetchTeacherProfile(user);
      localStorage.setItem('user', JSON.stringify(fullUser));
      navigate('/teacher-portal', { replace: true });
    } catch (err) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          'Teacher login failed.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: '#0f172a',
        backgroundImage: `
          radial-gradient(at 0% 0%, hsla(253,16%,7%,1) 0, transparent 50%),
          radial-gradient(at 50% 0%, hsla(225,39%,30%,1) 0, transparent 50%),
          radial-gradient(at 100% 0%, hsla(339,49%,30%,1) 0, transparent 50%)
        `,
        py: 4,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
          backgroundImage:
            'linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }}
      />
      <Container
        maxWidth="sm"
        sx={{ position: 'relative', zIndex: 1, maxWidth: { xs: '100%', sm: 480 } }}
      >
        <Card
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: '24px',
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 20px 60px rgba(0, 0, 0, 0.5)'
                : '0 20px 60px rgba(0, 0, 0, 0.15)',
            background:
              theme.palette.mode === 'dark'
                ? 'rgba(30, 41, 59, 0.9)'
                : 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(12px)',
            border:
              theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  background: '#f8fafc',
                  border: '1px solid #e5e7eb',
                  mx: 'auto',
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="School Logo"
                  sx={{ width: 42, height: 42, objectFit: 'contain' }}
                />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0,
                  }}
                >
                  Teacher Portal
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                  My School App
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 800,
                letterSpacing: 0,
                mb: 1,
                fontSize: { xs: '1.85rem', sm: '2.4rem' },
                lineHeight: 1.08,
              }}
            >
              Welcome back
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: '0.95rem', sm: '1rem' },
                fontWeight: 500,
                lineHeight: 1.6,
              }}
            >
              Sign in to access your teacher workspace.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.error.light}`,
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(244, 67, 54, 0.12)'
                      : 'rgba(244, 67, 54, 0.05)',
                  }}
                >
                  {error}
                </Alert>
              )}

              <TextField
                label="Teacher email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                fullWidth
                autoComplete="email"
                sx={textFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined color="primary" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                fullWidth
                autoComplete="current-password"
                sx={textFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword((current) => !current)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SchoolOutlined />
                  )
                }
                sx={{
                  py: 1.6,
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  backgroundColor: '#0f172a',
                  color: '#fff',
                  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.35)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 16px 34px rgba(15, 23, 42, 0.45)',
                    backgroundColor: '#111c33',
                  },
                  '&:active': {
                    transform: 'scale(0.97)',
                  },
                }}
              >
                {loading ? 'Signing in...' : 'Open Teacher Portal'}
              </Button>

              <Box sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  Use your teacher account credentials from the school administration.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}
